import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import get from "lodash/get";
import { accessToken } from "../shared/constants/localstorage.constant";
import type { UserPermissions } from "../api/permissions/permissions.types";
import { DEFAULT_PERMISSIONS } from "../api/permissions/permissions.types";
import { clearAuthSession } from "../utils/auth-session/clear-auth-session";
import { clearActiveTeamId } from "../utils/active-team/active-team";

export type { UserPermissions };

export interface ServiceLinePayloadAction {
  errorServiceId: string;
  nisLogId: string;
  service: ServiceDetails;
  serviceState: string;
}
export interface ServiceDetails {
  serviceId: number;
  name: string;
  pseudoId: string;
  serviceCode: string;
  description: string;
  parentServiceId?: number;
  isServiceStateActive: boolean;
  path: string;
  isServiceLineLoaded: boolean;
}

export interface FeatureSettings {
  [key: string]: "on" | "off";
}

export interface KycFeatureSettings {
  [key: string]: "on" | "off";
}

interface LocaleItem {
  code: string;
}

export interface I18nSettings {
  appLocales?: LocaleItem[];
  language?: string;
}

interface PersonalizedOptions {
  tabs?: {
    key: string;
    title: string;
    isEnabled: boolean;
    subTabs?: { key: string; title: string; isEnabled: boolean }[];
  }[];
}

interface PersonalisedOptions {
  company?: PersonalizedOptions;
  director?: PersonalizedOptions;
  shareholder?: PersonalizedOptions;
}

interface PersonalisedOptionsByCountry {
  [key: string]: PersonalisedOptions;
}

interface Preferences {
  bankVerificationPrefs?: {
    includePDFBankDetailsEnabled: boolean;
  };
  businessReportHeaderPrefs?: string;
  companyReportPrefs?: {
    autoFinancialTradeEnabled: boolean;
    includePDFBankDetailsEnabled: boolean;
    notesFeatureEnabled: boolean;
  };
  companySearchPrefs?: {
    searchPreviousNamesEnabled: boolean;
  };
  favouriteCountries?: string[];
  graydonEnterpriseIdPrefs?: boolean;
  pdfPrefs?: {
    deliveryMethod: string;
    hideUserName: boolean;
    pdfRowDataCount: number;
    personalisedOptions?: PersonalisedOptionsByCountry;
    reportDetail?: string;
  };

  reversedCCDSStatusHistory?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sidebarMenuPreferences?: any[];
}

export interface ActiveUser {
  email: string;
  firstName: string;
  isActive: true;
  surname: string;
  userId: number;
  username: string;
  websiteRole: string;
}

export interface UserState {
  userId: string | null;
  isSignedIn: boolean;
  username: string | null;
  language: string;
  name: {
    title: string | null;
    firstName: string | null;
    lastName: string | null;
  };
  favouriteCountries: string[];
  remainingCredits: number;
  services: ServiceDetails[];
  userDetailsError: boolean;
  owningEntity: string | null;
  user: object | null;
  isUpp: boolean;
  isServiceLinesLoaded: boolean;
  kycFeatures: KycFeatureSettings[];
  features: FeatureSettings;
  preferences: Preferences;
  i18n: I18nSettings;
  monitoringCountryCode: string[];
  isVisuraDownloading: boolean;
  globalUserAlertNotification: {
    profileName: string;
    internalId: string;
    showAlertNotification: boolean;
  };
  activeUsers: ActiveUser[];
  permissions: UserPermissions;
  isPermissionsLoaded: boolean;
}

export const userInitialState: UserState = {
  userId: null,
  isSignedIn: false,
  username: null,
  language: "en-US",
  name: {
    title: null,
    firstName: null,
    lastName: null,
  },
  favouriteCountries: [],
  remainingCredits: 0,
  services: [
    {
      serviceId: 3120,
      serviceCode: "KYCP",
      description: "KYC Protect - Know Your Customer",
      name: "KYC Protect",
      pseudoId: "kyc_protect",
      isServiceStateActive: false,
      path: "/protect",
      isServiceLineLoaded: false,
    },
  ],
  userDetailsError: false,
  owningEntity: null,
  user: {},
  isUpp: false,
  isServiceLinesLoaded: false,
  features: {},
  kycFeatures: [],
  preferences: {},
  i18n: {},
  monitoringCountryCode: [],
  isVisuraDownloading: false,
  globalUserAlertNotification: {
    profileName: "",
    internalId: "",
    showAlertNotification: false,
  },
  activeUsers: [],
  permissions: DEFAULT_PERMISSIONS,
  isPermissionsLoaded: false,
};

export const userSlice = createSlice({
  name: "user",
  initialState: userInitialState,
  reducers: {
    userSignedIn: (state, action: PayloadAction<string>) => {
      const hadAccessToken = Boolean(localStorage.getItem(accessToken));
      localStorage.setItem(accessToken, action.payload);

      // Only clear mid-session team on a fresh login (no prior token).
      // Page reload after team switch must keep the selected team.
      if (!hadAccessToken) {
        clearActiveTeamId();
      }

      return {
        ...state,
        isSignedIn: true,
      };
    },
    userSignedOut: () => {
      clearAuthSession();
      return userInitialState;
    },
    syncUserDataFromUpp: (state, action: PayloadAction<object>) => {
      return {
        ...state,
        user: get(action, "payload.restOfUppState.user", {}),
        isUpp: get(action, "payload.isUpp", false),
        features: get(action, "payload.restOfUppState.features", {}),
        i18n: get(action, "payload.restOfUppState.i18n", {}),
        preferences: get(action, "payload.restOfUppState.preferences", {}),
        isVisuraDownloading: get(
          action,
          "payload.restOfUppState.isVisuraDownloading",
          false
        ),
      };
    },
    updateUserServiceLines: (
      state,
      action: PayloadAction<ServiceLinePayloadAction>
    ) => {
      const serviceLine = state.services.find(
        (s) => s.serviceId === action.payload.service.serviceId
      );

      if (serviceLine) {
        serviceLine.isServiceStateActive =
          action.payload.serviceState === "ServiceValidAndRequireCharge";
        serviceLine.isServiceLineLoaded = true;
      }
    },
    updateUserServiceLoaded: (state) => {
      return {
        ...state,
        isServiceLinesLoaded: true,
      };
    },
    updateMonitoringCountries: (state, action: PayloadAction<string[]>) => {
      return {
        ...state,
        monitoringCountryCode: get(action, "payload", []),
      };
    },
    updateActiveUsers: (state, action: PayloadAction<ActiveUser[]>) => {
      return {
        ...state,
        activeUsers: get(action, "payload", []),
      };
    },
    updateFeatureFlags: (state, action: PayloadAction<FeatureSettings[]>) => {
      return {
        ...state,
        kycFeatures: action.payload,
      };
    },
    updateUserPermissions: (state, action: PayloadAction<UserPermissions>) => {
      return {
        ...state,
        permissions: action.payload,
        isPermissionsLoaded: true,
      };
    },
  },
});

export const {
  userSignedIn,
  userSignedOut,
  syncUserDataFromUpp,
  updateUserServiceLines,
  updateUserServiceLoaded,
  updateMonitoringCountries,
  updateActiveUsers,
  updateFeatureFlags,
  updateUserPermissions,
} = userSlice.actions;

export default userSlice.reducer;
